/**
 * Formulário de solicitação de compra do funcionário, em uma coluna e com cinco campos.
 *
 * Papel: recolhe foto opcional, descrição, fornecedor, quantidade e valor unitário,
 * valida com `novaSolicitacaoSchema` e envia. Depois de gravado, limpa os campos e
 * mostra a confirmação, e o histórico abaixo é atualizado.
 *
 * Como usar: não recebe props. Não há campo de categoria, canal, status nem
 * residência, porque a API os define no servidor e recusa um corpo que os traga.
 * A foto é lida por `handleArquivoDoItem`, de `@/modules/compras`, o mesmo tratamento
 * da foto de item do pedido de compra: a imagem passa pelo limite do schema antes de
 * entrar no formulário.
 *
 * Os valores iniciais são estáticos e vão para o `useForm` uma vez. A confirmação
 * some assim que a pessoa mexe em qualquer campo, para não ficar na tela sobre um
 * formulário que já é outro.
 */
import { type NovaSolicitacao, novaSolicitacaoSchema } from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ChangeEvent, useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { handleArquivoDoItem } from '@/modules/compras';
import { aplicarErroDaApi, tradutorPorMapa } from '@/shared/lib/erro-de-formulario';
import { Button } from '@/shared/ui/button';
import { CampoMascarado } from '@/shared/ui/campo-mascarado';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';

import { useCriarSolicitacao } from '../hooks/usar-solicitacoes';
import { type FormularioSolicitacao as ValoresDoFormulario } from '../types';

/** Valores com que o formulário abre e volta depois de um envio gravado. */
const VALORES_INICIAIS: ValoresDoFormulario = {
  foto: null,
  descricao: '',
  fornecedor: '',
  quantidade: '',
  valorUnitario: '',
};

/**
 * Liga o nome da API ao campo do formulário, para o erro cair no campo certo.
 *
 * Os nomes são os mesmos do schema, por isso o tradutor só confirma o que já
 * coincide; um campo que a API devolver com outro nome fica sem mensagem de campo e
 * vai para o aviso geral acima dos botões.
 */
const traduzirCampo = tradutorPorMapa<ValoresDoFormulario>({
  foto: 'foto',
  descricao: 'descricao',
  fornecedor: 'fornecedor',
  quantidade: 'quantidade',
  valorUnitario: 'valorUnitario',
});

/**
 * Mensagem de um campo recusado, ligada ao controle por `aria-describedby`.
 *
 * @param props.id - Identificador do parágrafo, referenciado pelo controle.
 * @param props.mensagem - Texto do erro; `undefined` não desenha nada.
 * @returns O parágrafo de erro, ou `null` quando o campo está válido.
 */
function MensagemDeErro({ id, mensagem }: { id: string; mensagem: string | undefined }) {
  if (mensagem === undefined) return null;
  return (
    <p id={id} className="text-sm text-destructive">
      {mensagem}
    </p>
  );
}

/**
 * Formulário de envio da solicitação de compra.
 *
 * O `zodResolver` converte quantidade e valor de texto para número antes de o envio
 * acontecer, então `handleEnviar` recebe `NovaSolicitacao` e não precisa checar nada.
 * Um erro da API volta para o campo que ela aponta, ou para o aviso geral quando não
 * aponta nenhum.
 *
 * @returns O formulário, a confirmação de envio e o aviso de erro do envio.
 */
export function FormularioSolicitacao() {
  const idDoFormulario = useId();
  const form = useForm<ValoresDoFormulario, unknown, NovaSolicitacao>({
    resolver: zodResolver(novaSolicitacaoSchema),
    defaultValues: VALORES_INICIAIS,
  });
  const envio = useCriarSolicitacao();
  const [erroDeEnvio, setErroDeEnvio] = useState<string | null>(null);
  // Trocar a chave do campo de arquivo é o que o esvazia: um `<input type="file">`
  // não aceita limpeza por código, e sem isso escolher o mesmo arquivo de novo não
  // dispararia `onChange`.
  const [versaoDoArquivo, setVersaoDoArquivo] = useState(0);
  const confirmado = envio.isSuccess && !form.formState.isDirty;
  const { errors } = form.formState;

  const idDescricao = `${idDoFormulario}-descricao`;
  const idFornecedor = `${idDoFormulario}-fornecedor`;
  const idQuantidade = `${idDoFormulario}-quantidade`;
  const idValor = `${idDoFormulario}-valor`;
  const idFoto = `${idDoFormulario}-foto`;

  /**
   * Envia os campos validados e, ao gravar, devolve o formulário ao estado inicial.
   *
   * @param dados - Os cinco campos já convertidos pelo schema.
   */
  function handleEnviar(dados: NovaSolicitacao) {
    setErroDeEnvio(null);
    envio.mutate(dados, {
      onSuccess: () => {
        form.reset(VALORES_INICIAIS);
        setVersaoDoArquivo((versao) => versao + 1);
      },
      onError: (erro) => {
        setErroDeEnvio(aplicarErroDaApi(erro, form, traduzirCampo));
      },
    });
  }

  /**
   * Recebe o arquivo escolhido e o põe no campo `foto`, ou mostra por que não serve.
   *
   * @param evento - Troca do campo de arquivo de foto.
   */
  function handleFotoEscolhida(evento: ChangeEvent<HTMLInputElement>) {
    handleArquivoDoItem(
      evento,
      (imagem) => {
        form.clearErrors('foto');
        form.setValue('foto', imagem, { shouldDirty: true });
      },
      (mensagem) => form.setError('foto', { message: mensagem }),
    );
  }

  /** Tira a foto escolhida, sem gravar nada até o envio. */
  function handleRemoverFoto() {
    form.clearErrors('foto');
    form.setValue('foto', null, { shouldDirty: true });
    setVersaoDoArquivo((versao) => versao + 1);
  }

  return (
    <form
      onSubmit={form.handleSubmit(handleEnviar)}
      noValidate
      className="flex w-full max-w-md flex-col gap-5"
    >
      <Controller
        control={form.control}
        name="foto"
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <label className="flex min-h-11 cursor-pointer flex-col items-center gap-2 overflow-hidden rounded-md border border-dashed border-linha-forte bg-superficie p-3 text-center text-sm text-texto-fraco transition-colors focus-within:ring-2 focus-within:ring-ring hover:border-brass hover:text-brass-light">
              <input
                key={versaoDoArquivo}
                ref={field.ref}
                id={idFoto}
                type="file"
                accept="image/*"
                capture="environment"
                aria-label="Foto (opcional)"
                aria-describedby={errors.foto ? `${idFoto}-erro` : undefined}
                aria-invalid={errors.foto ? true : undefined}
                className="sr-only"
                onChange={handleFotoEscolhida}
              />
              {typeof field.value === 'string' && field.value !== '' && (
                <img
                  src={field.value}
                  alt="Foto escolhida"
                  className="h-40 w-full rounded-sm object-contain"
                />
              )}
              <span>
                {typeof field.value === 'string' && field.value !== ''
                  ? 'Trocar foto'
                  : 'Tirar foto ou escolher imagem (opcional)'}
              </span>
            </label>
            {typeof field.value === 'string' && field.value !== '' && (
              <Button
                type="button"
                variant="outline"
                className="min-h-11"
                onClick={handleRemoverFoto}
              >
                Remover foto
              </Button>
            )}
            <MensagemDeErro id={`${idFoto}-erro`} mensagem={errors.foto?.message} />
          </div>
        )}
      />

      <div className="flex flex-col gap-2">
        <Label htmlFor={idDescricao}>Descrição</Label>
        <Textarea
          id={idDescricao}
          rows={2}
          aria-invalid={errors.descricao ? true : undefined}
          aria-describedby={errors.descricao ? `${idDescricao}-erro` : undefined}
          className="min-h-11"
          {...form.register('descricao')}
        />
        <MensagemDeErro id={`${idDescricao}-erro`} mensagem={errors.descricao?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={idFornecedor}>Fornecedor</Label>
        <Input
          id={idFornecedor}
          aria-invalid={errors.fornecedor ? true : undefined}
          aria-describedby={errors.fornecedor ? `${idFornecedor}-erro` : undefined}
          className="min-h-11"
          {...form.register('fornecedor')}
        />
        <MensagemDeErro id={`${idFornecedor}-erro`} mensagem={errors.fornecedor?.message} />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor={idQuantidade}>Quantidade</Label>
        <Input
          id={idQuantidade}
          inputMode="numeric"
          aria-invalid={errors.quantidade ? true : undefined}
          aria-describedby={errors.quantidade ? `${idQuantidade}-erro` : undefined}
          className="min-h-11"
          {...form.register('quantidade')}
        />
        <MensagemDeErro id={`${idQuantidade}-erro`} mensagem={errors.quantidade?.message} />
      </div>

      <Controller
        control={form.control}
        name="valorUnitario"
        render={({ field }) => (
          <div className="flex flex-col gap-2">
            <Label htmlFor={idValor}>Valor unitário (R$)</Label>
            <CampoMascarado
              id={idValor}
              formato="dinheiro"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              aria-invalid={errors.valorUnitario ? true : undefined}
              aria-describedby={errors.valorUnitario ? `${idValor}-erro` : undefined}
              className="min-h-11"
            />
            <MensagemDeErro id={`${idValor}-erro`} mensagem={errors.valorUnitario?.message} />
          </div>
        )}
      />

      {confirmado && (
        <p
          role="status"
          className="rounded-md border border-sucesso bg-superficie p-3 text-sm"
        >
          Solicitação enviada. Ela aparece no histórico abaixo.
        </p>
      )}

      {erroDeEnvio !== null && (
        <p role="alert" className="text-sm text-destructive">
          {erroDeEnvio}
        </p>
      )}

      <Button type="submit" className="min-h-11 w-full" disabled={envio.isPending}>
        {envio.isPending ? 'Enviando…' : 'Enviar solicitação'}
      </Button>
    </form>
  );
}
